import { AddCircle } from '@mui/icons-material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Alert,
    Box,
    Button,
    Collapse,
    Divider,
    FormHelperText,
    Grid,
    InputLabel,
    lighten,
    Paper,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { get, useFormContext, useWatch } from 'react-hook-form';
import { TransitionGroup } from 'react-transition-group';
import { useGetPricingTemplatesQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../../layout/styledcomponents/CustomButton';
import { theme } from '../../../../../shared/theme';
import {
    camelCaseToSentenceCase,
    formatCurrencyShort,
} from '../../../../../shared/utils';
import { ServiceType, Status } from '../../../../enums/contractor';
import CustomTooltip from '../../../../shared/CustomTooltip';
import FormAutocomplete from '../../../../shared/FormAutocomplete';
import FormMultiAutocomplete from '../../../../shared/FormMultiAutocomplete';
import FormSelect from '../../../../shared/FormSelect';
import FormTextField from '../../../../shared/FormTextField';
import { AvailableDays, ModuleContractor } from '../../../../types/contractor';
import { BookingSetMenu, SetMenu } from '../../../../types/set-menu';
import { getAvailableTimes } from '../../../Contractors/utils/availability.utils';
import {
    getContractorBusinessDayNumbers,
    getEffectiveJobRequestCutoffDays,
    isBeforeJobRequestCutoff,
    isJobDateUnavailable,
} from '../../../Contractors/utils/cutoff.utils';
import { getUnavailableDays } from '../../../Profile/Form/Contractor/Availability';
import {
    reservationEditDescriptionSx,
    reservationEditLabelSx,
    reservationEditValueSx,
} from '../../EditReservation/reservationEditTypography';
import JobMenuModal from './JobMenuModal';

import {
    Job,
    JobItem,
    ProviderType,
} from '../../../../../app/Slices/SevenDayApiTypes';
import FormHiddenField from '../../../../shared/FormHiddenField';
import { Experience, FormValues } from '../../../../types/experience';
import { TimeArray } from '../../../Experiences/Owner/Property/TimeArray';
import {
    compareProviderServiceTypes,
    OWNER_SET_MENU_CONTRACTOR_ID_ALCOHOL,
    OWNER_SET_MENU_CONTRACTOR_ID_CATERING,
} from './reservationJobs.helpers';

export type SetMenuAsProvider = Omit<ModuleContractor, 'id'> & {
    id: string | number;
};

export const groupJobItemsByTemplate = (items: JobItem[]) => {
    const map: {
        [key: string]: {
            template: { id: number; name: string };
            jobItems: JobItem[];
        };
    } = {};

    for (const item of items) {
        const key = item.template.id;

        if (!map[key]) {
            map[key] = {
                template: item.template,
                jobItems: [],
            };
        }

        map[key].jobItems.push(item);
    }

    return Object.values(map);
};

const sectionTitleSx = {
    ...reservationEditLabelSx,
    display: 'block' as const,
    mb: 1.5,
};

const ReservationJobForm = ({
    name,
    experienceDetails,
    dateOptions,
    id,
    readOnly = false,
    isItineraryForm = false,
    jobsArray,
    providers,
    numberOfGuests,
}: {
    name: string;
    experienceDetails: Experience;
    dateOptions: string[];
    numberOfJobs: number;
    index: number;
    id: string | number;
    isEditing?: boolean;
    readOnly?: boolean;
    isItineraryForm?: boolean;
    jobsArray: (Job | BookingSetMenu)[];
    providers: (ModuleContractor | SetMenuAsProvider)[];
    numberOfGuests?: number;
}) => {
    const methods = useFormContext();
    const [showMenu, setShowMenu] = useState(false);
    const isMobile = useMediaQuery('(max-width: 770px)');
    const [showContractorChangeWarning, setShowContractorChangeWarning] =
        useState(false);
    const [pendingContractor, setPendingContractor] = useState<{
        id: number | string;
        name: string;
    } | null>(null);

    const {
        control,
        setValue,
        watch,
        getValues,
        trigger,
        formState: { errors },
    } = methods;

    const error = get(errors, name);

    const currentItems = useWatch({ control, name: `${name}.items` }) ?? [];
    const currentContractorId = useWatch({
        control,
        name: `${name}.contractor.id`,
    });
    const currentTemplateId = useWatch({ control, name: `${name}.templateId` });
    const currentJob = useWatch({ control, name: `${name}` });
    const status = currentJob?.status;
    const currentJobDate = useWatch({ control, name: `${name}.jobDate` });
    const currentStartTime = useWatch({ control, name: `${name}.startTime` });
    const currentEndTime = useWatch({ control, name: `${name}.endTime` });
    const quantity = useWatch({ control, name: `${name}.quantity` });
    const providerType = useWatch({ control, name: `${name}.providerType` });

    const selectedSetMenu = experienceDetails?.setMenus?.find(
        (setMenu: SetMenu) => setMenu.id === currentJob?.setMenu?.id,
    );

    const { data: templates, isLoading: isLoadingPricingTemplates } =
        useGetPricingTemplatesQuery(currentContractorId, {
            skip:
                !currentContractorId ||
                providerType !== ProviderType.Contractor,
        });

    const hasSetMenus = (experienceDetails?.setMenus?.length ?? 0) > 0;

    const selectedProvider = providers.find(
        (c) => c.id === currentContractorId,
    );

    const providerOptions = providers
        .map((contractor) => ({
            value: contractor.id,
            label: contractor.name,
            name: contractor.name,
            groupLabel: contractor.services.type,
        }))
        .sort((a, b) =>
            compareProviderServiceTypes(a.groupLabel, b.groupLabel),
        );

    const handleDelete = () => {
        if (readOnly) return;
        const jobsRecord = getValues('jobs') ?? {};
        const updatedJobs = { ...jobsRecord };
        delete updatedJobs[id];
        setValue('jobs', updatedJobs, { shouldDirty: true });
    };

    const handleMenu = () => {
        if (readOnly || !currentContractorId) return;
        setShowMenu((isOpen) => !isOpen);
    };

    const modifiedItems = currentItems.map((item: JobItem) => ({
        ...item,
        total: item.cost * item.quantity,
    }));

    let itemsGroupedByTemplate;
    if (providerType === ProviderType.Contractor) {
        itemsGroupedByTemplate = groupJobItemsByTemplate(modifiedItems);
    }

    const handleAddItem = (item: JobItem) => {
        const newValues = currentItems.map((i: JobItem) => {
            const foundItem = currentItems.find((x: JobItem) => {
                if (item.pricingItem && x.pricingItem) {
                    return x.pricingItem.id === item.pricingItem.id;
                }
                return item.id === x.id;
            });

            return i === foundItem ? { ...i, quantity: i.quantity + 1 } : i;
        });

        setValue(`${name}.items`, newValues, {
            shouldDirty: true,
            shouldValidate: true,
        });
        syncJobDateWithCutoff(newValues);
    };

    const handleDecrementItem = (item: JobItem) => {
        let newValues = [];

        if (item.quantity <= 1) {
            newValues = currentItems.filter((i: JobItem) => {
                if (i.pricingItem && item.pricingItem) {
                    return i.pricingItem.id !== item.pricingItem.id;
                }
                return item.id !== i.id;
            });
        } else {
            newValues = currentItems.map((i: JobItem) => {
                const foundItem = currentItems.find((x: JobItem) => {
                    if (item.pricingItem && x.pricingItem) {
                        return x.pricingItem.id === item.pricingItem.id;
                    }
                    return item.id === x.id;
                });

                return i === foundItem ? { ...i, quantity: i.quantity - 1 } : i;
            });
        }

        setValue(`${name}.items`, newValues, {
            shouldDirty: true,
            shouldValidate: true,
        });
        syncJobDateWithCutoff(newValues);
    };

    const jobTotal =
        modifiedItems.reduce(
            (acc: number, item: JobItem) => acc + item.total,
            0,
        ) || (selectedSetMenu ? selectedSetMenu.pricePerPerson * quantity : 0);

    const cannotAddAnotherService = readOnly || !currentContractorId;

    const handleContractorChange = (event: SelectChangeEvent<unknown>) => {
        const newContractorId = event.target.value as string | number;

        const targetContractor = providers.find(
            (contractor) => contractor.id === newContractorId,
        );

        setValue(`${name}.jobDate`, '', {
            shouldDirty: true,
        });
        setValue(`${name}.serviceType`, targetContractor?.services.type, {
            shouldDirty: true,
        });

        if (targetContractor && targetContractor.cancellationPolicy) {
            const policyAlreadyChecked = !!jobsArray.find(
                (job) =>
                    job.providerType === ProviderType.Contractor &&
                    job.contractor?.id === newContractorId &&
                    job.policy?.checked &&
                    ![Status.Declined, Status.Cancelled].includes(job.status),
            );
            setValue(`${name}.policy`, { checked: policyAlreadyChecked });
        } else {
            setValue(`${name}.policy`, undefined);
        }

        if (
            newContractorId === OWNER_SET_MENU_CONTRACTOR_ID_CATERING ||
            newContractorId === OWNER_SET_MENU_CONTRACTOR_ID_ALCOHOL
        ) {
            setValue(`${name}.providerType`, ProviderType.Owner, {
                shouldDirty: true,
                shouldValidate: true,
            });
            setValue(`${name}.setMenu`, null);
            setValue(`${name}.templateId`, '');
            setValue(`${name}.quantity`, undefined);
            setValue(`${name}.pricePerPerson`, undefined);
        } else {
            setValue(`${name}.providerType`, ProviderType.Contractor, {
                shouldDirty: true,
                shouldValidate: true,
            });
            setValue(`${name}.setMenu`, null);
            setValue(`${name}.templateId`, '');
            setValue(`${name}.quantity`, undefined);
            setValue(`${name}.pricePerPerson`, undefined);
        }

        if (!currentItems.length) {
            return;
        }

        const newContractor = providers.find(
            (provider) => provider.id === newContractorId,
        );
        setPendingContractor({
            id: newContractorId,
            name: newContractor?.name ?? '',
        });
        setShowContractorChangeWarning(true);

        const target = event.target as { value: number };
        target.value = currentContractorId as number;
    };

    const handleConfirmContractorChange = () => {
        if (pendingContractor === null) {
            setShowContractorChangeWarning(false);
            return;
        }

        setValue(`${name}.contractor.id`, pendingContractor.id, {
            shouldDirty: true,
            shouldValidate: true,
            shouldTouch: true,
        });
        setValue(`${name}.contractor.name`, pendingContractor.name, {
            shouldDirty: true,
        });

        if (
            pendingContractor.id === OWNER_SET_MENU_CONTRACTOR_ID_CATERING ||
            pendingContractor.id === OWNER_SET_MENU_CONTRACTOR_ID_ALCOHOL
        ) {
            setValue(`${name}.providerType`, ProviderType.Owner, {
                shouldDirty: true,
                shouldValidate: true,
            });
            setValue(`${name}.setMenu`, null);
            setValue(`${name}.templateId`, '');
            setValue(`${name}.quantity`, undefined);
            setValue(`${name}.pricePerPerson`, undefined);
        } else {
            setValue(`${name}.providerType`, ProviderType.Contractor, {
                shouldDirty: true,
                shouldValidate: true,
            });
            setValue(`${name}.setMenu`, null);
            setValue(`${name}.templateId`, '');
            setValue(`${name}.quantity`, undefined);
            setValue(`${name}.pricePerPerson`, undefined);
        }

        setValue(`${name}.items`, [], {
            shouldDirty: true,
            shouldValidate: true,
        });

        setPendingContractor(null);
        setShowContractorChangeWarning(false);
    };

    const handleCancelContractorChange = () => {
        setPendingContractor(null);
        setShowContractorChangeWarning(false);
    };

    const notesPlaceholder =
        templates && templates.length > 0
            ? 'E.g. additional details or special requests.'
            : 'Describe the services you need, including quantity and timing.';

    const primaryMenuActionLabel =
        modifiedItems.length === 0
            ? currentContractorId === OWNER_SET_MENU_CONTRACTOR_ID_CATERING ||
              currentContractorId === OWNER_SET_MENU_CONTRACTOR_ID_ALCOHOL
                ? 'Select Set Menu'
                : selectedProvider?.services.type === ServiceType.Wellness
                  ? 'Add treatments'
                  : selectedProvider?.services.type === ServiceType.Catering
                    ? 'Add menu items'
                    : 'Add services'
            : 'Add another service';

    const services = selectedProvider?.services?.items;
    const unavailablePeriods = selectedProvider?.unavailablePeriods || [];
    const unavailableDays = getUnavailableDays(
        selectedProvider?.availableDays.map(
            (availability: AvailableDays) => availability.day,
        ) || [],
    );
    const businessDayNumbers = getContractorBusinessDayNumbers(
        selectedProvider?.availableDays,
    );
    const effectiveCutoffDays = getEffectiveJobRequestCutoffDays({
        contractorCutoff: selectedProvider?.cutoff,
        templates: templates ?? [],
        templateId: currentTemplateId,
        items: currentItems,
    });

    const syncJobDateWithCutoff = (items: JobItem[]) => {
        const jobDate = getValues(`${name}.jobDate`);
        if (!jobDate) return;

        const cutoff = getEffectiveJobRequestCutoffDays({
            contractorCutoff: selectedProvider?.cutoff,
            templates: templates ?? [],
            templateId: currentTemplateId,
            items,
        });

        if (isBeforeJobRequestCutoff(jobDate, cutoff, businessDayNumbers)) {
            setValue(`${name}.jobDate`, '', {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    };

    const getDateDisableReason = (
        date: string,
    ): 'unavailable' | 'cutoff' | null => {
        if (isJobDateUnavailable(date, unavailablePeriods, unavailableDays)) {
            return 'unavailable';
        }

        if (
            isBeforeJobRequestCutoff(
                date,
                effectiveCutoffDays,
                businessDayNumbers,
            )
        ) {
            return 'cutoff';
        }

        return null;
    };

    const isDateDisabled = (date: string) =>
        getDateDisableReason(date) !== null;

    const hasPricingTemplates = templates && templates.length > 0;

    useEffect(() => {
        setValue(`${name}.contractorHasTemplates`, hasPricingTemplates, {
            shouldDirty: true,
            shouldValidate: true,
        });
    }, [hasPricingTemplates, name, setValue]);

    let startTimes: { value: string; label: string }[] = TimeArray;
    let endTimes: { value: string; label: string }[] = TimeArray;

    if (selectedProvider) {
        const result = getAvailableTimes(
            selectedProvider,
            currentJobDate,
            currentStartTime,
            currentEndTime,
        );
        startTimes = result.startTimes;
        endTimes = result.endTimes;
    }

    const bookingAvailability = useMemo(
        () => ({
            dateOptions,
            contractorCutoff: selectedProvider?.cutoff,
            businessDayNumbers,
            unavailablePeriods,
            unavailableDays,
        }),
        [
            dateOptions,
            selectedProvider?.cutoff,
            businessDayNumbers,
            unavailablePeriods,
            unavailableDays,
        ],
    );

    const handleDecrementSetmenuQuantity = () => {
        if (quantity <= 1) {
            setValue(`${name}.setMenu`, null);
        } else {
            setValue(`${name}.quantity`, quantity - 1);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box>
                {!isItineraryForm && (
                    <Typography variant='body3' sx={sectionTitleSx}>
                        Service details
                    </Typography>
                )}
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel
                            required
                            htmlFor={`${name}-contractor-input`}
                            id={`${name}-contractor-label`}
                            sx={reservationEditLabelSx}
                        >
                            Provider
                        </InputLabel>
                        <FormSelect
                            id={`${name}-contractor-input`}
                            labelId={`${name}-contractor-label`}
                            name={`${name}.contractor.id`}
                            control={control}
                            options={providerOptions}
                            onChange={handleContractorChange}
                            disabled={readOnly || showContractorChangeWarning}
                            groupedMenu={true}
                        />
                        <Collapse in={showContractorChangeWarning}>
                            <Alert
                                severity='warning'
                                variant='outlined'
                                sx={{ mt: 0.5 }}
                                action={
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            size='small'
                                            color='inherit'
                                            onClick={
                                                handleCancelContractorChange
                                            }
                                        >
                                            Keep items
                                        </Button>
                                        <Button
                                            size='small'
                                            color='warning'
                                            variant='contained'
                                            onClick={
                                                handleConfirmContractorChange
                                            }
                                        >
                                            Change contractor
                                        </Button>
                                    </Box>
                                }
                            >
                                Changing the contractor will remove all selected
                                items. Continue?
                            </Alert>
                        </Collapse>
                    </Grid>
                    {modifiedItems.length > 0 && (
                        <Grid item xs={12}>
                            <InputLabel sx={reservationEditLabelSx}>
                                Selected Items
                            </InputLabel>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                }}
                            >
                                <TransitionGroup
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '12px',
                                    }}
                                >
                                    {itemsGroupedByTemplate &&
                                        itemsGroupedByTemplate.map((group) => (
                                            <Collapse key={group.template.id}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <TransitionGroup
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection:
                                                                'column',
                                                            gap: '10px',
                                                        }}
                                                    >
                                                        {group.jobItems.map(
                                                            (
                                                                item: JobItem,
                                                                i: number,
                                                            ) => (
                                                                <Collapse
                                                                    key={i}
                                                                >
                                                                    <Paper
                                                                        variant='outlined'
                                                                        sx={{
                                                                            p: 1.5,
                                                                            borderRadius: 2,
                                                                            borderColor:
                                                                                'custom.lightBorderColor',
                                                                            boxShadow:
                                                                                'none',
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                display:
                                                                                    'flex',
                                                                                flexDirection:
                                                                                    {
                                                                                        xs: 'column',
                                                                                        sm: 'row',
                                                                                    },
                                                                                gap: 1.5,
                                                                                alignItems:
                                                                                    {
                                                                                        xs: 'flex-start',
                                                                                        sm: 'center',
                                                                                    },
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                sx={{
                                                                                    minWidth: 0,
                                                                                    flex: 1,
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    variant='body3'
                                                                                    sx={{
                                                                                        ...reservationEditValueSx,
                                                                                        fontWeight: 700,
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        item.name
                                                                                    }
                                                                                </Typography>
                                                                                <Typography
                                                                                    variant='body3'
                                                                                    sx={{
                                                                                        ...reservationEditDescriptionSx,
                                                                                        mt: 0.25,
                                                                                    }}
                                                                                >
                                                                                    {`${formatCurrencyShort(item.cost)} (ex. GST)`}
                                                                                </Typography>
                                                                                {item.description ? (
                                                                                    <Typography
                                                                                        variant='body3'
                                                                                        sx={{
                                                                                            ...reservationEditDescriptionSx,
                                                                                            mt: 0.75,
                                                                                        }}
                                                                                    >
                                                                                        {
                                                                                            item.description
                                                                                        }
                                                                                    </Typography>
                                                                                ) : null}
                                                                            </Box>

                                                                            <Typography
                                                                                variant='body3'
                                                                                sx={{
                                                                                    ...reservationEditValueSx,
                                                                                    fontWeight: 700,
                                                                                    whiteSpace:
                                                                                        'nowrap',
                                                                                    ml: {
                                                                                        sm: 'auto',
                                                                                    },
                                                                                }}
                                                                            >
                                                                                {formatCurrencyShort(
                                                                                    item.total,
                                                                                )}
                                                                            </Typography>

                                                                            <Box
                                                                                sx={{
                                                                                    display:
                                                                                        'flex',
                                                                                    alignItems:
                                                                                        'center',
                                                                                    justifyContent:
                                                                                        'space-between',
                                                                                    height: 38,
                                                                                    minWidth: 112,
                                                                                    overflow:
                                                                                        'hidden',
                                                                                    border: '1px solid',
                                                                                    borderColor:
                                                                                        'custom.lightBorderColor',
                                                                                    borderRadius: 999,
                                                                                    px: 0.5,
                                                                                    backgroundColor:
                                                                                        'background.paper',
                                                                                }}
                                                                            >
                                                                                <Button
                                                                                    variant='text'
                                                                                    size='small'
                                                                                    disabled={
                                                                                        readOnly
                                                                                    }
                                                                                    onClick={() =>
                                                                                        handleDecrementItem(
                                                                                            item,
                                                                                        )
                                                                                    }
                                                                                    sx={{
                                                                                        minWidth: 0,
                                                                                        width: 34,
                                                                                        height: 34,
                                                                                        borderRadius:
                                                                                            '50%',
                                                                                        p: 0,
                                                                                    }}
                                                                                >
                                                                                    {item.quantity >
                                                                                    1 ? (
                                                                                        <Typography
                                                                                            variant='body3'
                                                                                            fontWeight={
                                                                                                700
                                                                                            }
                                                                                        >
                                                                                            -
                                                                                        </Typography>
                                                                                    ) : (
                                                                                        <DeleteOutlineIcon
                                                                                            sx={{
                                                                                                fontSize: 18,
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                </Button>

                                                                                <Typography
                                                                                    variant='body3'
                                                                                    sx={{
                                                                                        fontWeight: 700,
                                                                                        minWidth: 20,
                                                                                        textAlign:
                                                                                            'center',
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        item.quantity
                                                                                    }
                                                                                </Typography>

                                                                                <Button
                                                                                    variant='text'
                                                                                    size='small'
                                                                                    disabled={
                                                                                        readOnly
                                                                                    }
                                                                                    onClick={() =>
                                                                                        handleAddItem(
                                                                                            item,
                                                                                        )
                                                                                    }
                                                                                    sx={{
                                                                                        minWidth: 0,
                                                                                        width: 34,
                                                                                        height: 34,
                                                                                        borderRadius:
                                                                                            '50%',
                                                                                        p: 0,
                                                                                    }}
                                                                                >
                                                                                    <Typography
                                                                                        variant='body3'
                                                                                        fontWeight={
                                                                                            700
                                                                                        }
                                                                                    >
                                                                                        +
                                                                                    </Typography>
                                                                                </Button>
                                                                            </Box>
                                                                        </Box>
                                                                    </Paper>
                                                                </Collapse>
                                                            ),
                                                        )}
                                                    </TransitionGroup>
                                                </Box>
                                            </Collapse>
                                        ))}
                                </TransitionGroup>
                            </Box>
                        </Grid>
                    )}
                    {error?.items && (
                        <FormHelperText error>
                            {error.items.message}
                        </FormHelperText>
                    )}
                    {(hasPricingTemplates ||
                        (hasSetMenus && providerType === ProviderType.Owner)) &&
                        !readOnly &&
                        selectedProvider &&
                        !isLoadingPricingTemplates && (
                            <Grid item xs={12}>
                                <FormHiddenField
                                    name={`${name}.primaryActionField`}
                                    control={control}
                                />
                                <CustomButton
                                    variant='outlined'
                                    color='primary'
                                    onClick={handleMenu}
                                    fullWidth={isMobile}
                                    disabled={
                                        cannotAddAnotherService ||
                                        !currentContractorId
                                    }
                                    startIcon={<AddCircle />}
                                >
                                    {primaryMenuActionLabel}
                                </CustomButton>
                                {providerType === ProviderType.Contractor && (
                                    <Typography
                                        sx={{
                                            fontStyle: 'italic',
                                            fontSize: '12px',
                                            mt: 0.5,
                                        }}
                                    >
                                        * Selected services may affect
                                        availability
                                    </Typography>
                                )}

                                {cannotAddAnotherService ? (
                                    <FormHelperText error sx={{ mt: 0.75 }}>
                                        {readOnly
                                            ? 'Unable to modify a cancelled/declined job.'
                                            : 'Complete contractor, date, start time, and end time before adding another service.'}
                                    </FormHelperText>
                                ) : null}
                            </Grid>
                        )}
                    <Grid item xs={12} sm={4}>
                        <InputLabel
                            required
                            htmlFor={`${name}-date-input`}
                            sx={reservationEditLabelSx}
                        >
                            Date
                        </InputLabel>
                        <FormAutocomplete
                            id={`${name}-date-input`}
                            name={`${name}.jobDate`}
                            control={control}
                            options={dateOptions.map((date) => {
                                const disableReason =
                                    getDateDisableReason(date);
                                const suffix =
                                    disableReason === 'unavailable'
                                        ? ' (Unavailable)'
                                        : disableReason === 'cutoff'
                                          ? ' (Cut-off)'
                                          : '';

                                return {
                                    value: date,
                                    label: `${moment(date).format('DD/MM/YYYY')}${suffix}`,
                                };
                            })}
                            disabled={readOnly || !selectedProvider}
                            placeholder={
                                !selectedProvider ? 'Select contractor' : ''
                            }
                            getOptionDisabled={(option) =>
                                isDateDisabled(option.value)
                            }
                            fontSize='14px'
                        />
                    </Grid>

                    <Grid item xs={6} sm={4}>
                        <InputLabel
                            required
                            htmlFor={`${name}-start-time-input`}
                            sx={reservationEditLabelSx}
                        >
                            Start Time
                        </InputLabel>
                        <FormAutocomplete
                            id={`${name}-start-time-input`}
                            name={`${name}.startTime`}
                            control={control}
                            options={startTimes}
                            disabled={
                                readOnly || !selectedProvider || !currentJobDate
                            }
                            placeholder={
                                !selectedProvider
                                    ? 'Select contractor'
                                    : !currentJobDate
                                      ? 'Select date'
                                      : ''
                            }
                        />
                    </Grid>

                    <Grid item xs={6} sm={4}>
                        <InputLabel
                            required
                            htmlFor={`${name}-end-time-input`}
                            sx={reservationEditLabelSx}
                        >
                            End Time
                        </InputLabel>
                        <FormAutocomplete
                            id={`${name}-end-time-input`}
                            name={`${name}.endTime`}
                            control={control}
                            options={endTimes}
                            disabled={
                                readOnly ||
                                !selectedProvider ||
                                !currentJobDate ||
                                !currentStartTime
                            }
                            placeholder={
                                !selectedProvider
                                    ? 'Select contractor'
                                    : !currentJobDate
                                      ? 'Select date'
                                      : !currentStartTime
                                        ? 'Select start time'
                                        : ''
                            }
                        />
                    </Grid>

                    {selectedProvider &&
                        !isLoadingPricingTemplates &&
                        selectedProvider.id !==
                            OWNER_SET_MENU_CONTRACTOR_ID_CATERING &&
                        selectedProvider.id !==
                            OWNER_SET_MENU_CONTRACTOR_ID_ALCOHOL &&
                        !hasPricingTemplates && (
                            <Grid item xs={12}>
                                <InputLabel
                                    required
                                    sx={reservationEditLabelSx}
                                >
                                    Services
                                </InputLabel>
                                <FormMultiAutocomplete
                                    name={`${name}.services`}
                                    control={control}
                                    fullWidth
                                    disabled={readOnly || !selectedProvider}
                                    placeholder={
                                        services ? '' : 'Select contractor'
                                    }
                                    options={
                                        services?.map((service: string) => ({
                                            value: service,
                                            label: camelCaseToSentenceCase(
                                                service,
                                            ),
                                        })) || []
                                    }
                                />
                            </Grid>
                        )}
                </Grid>
            </Box>
            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 1,
                    }}
                >
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                        <InputLabel
                            required={!templates?.length}
                            htmlFor={`${name}-notes-input`}
                        >
                            Notes
                        </InputLabel>
                        {templates && templates.length === 0 && (
                            <CustomTooltip
                                title={
                                    <Typography
                                        variant='body3'
                                        sx={reservationEditDescriptionSx}
                                    >
                                        This contractor doesn’t have a pricing
                                        menu available. Please type in your
                                        requirements.
                                    </Typography>
                                }
                                placement='right'
                            >
                                <HelpOutlineIcon
                                    sx={{
                                        color: 'text.primary',
                                        fontSize: 16,
                                        mt: '-6px',
                                    }}
                                />
                            </CustomTooltip>
                        )}
                    </Box>

                    {watch(`${name}.guestNotes`)?.length > 0 && (
                        <Typography
                            variant='body3'
                            sx={{
                                ...reservationEditDescriptionSx,
                                fontSize: 12,
                            }}
                        >
                            Characters left:{' '}
                            {300 - watch(`${name}.guestNotes`).length}
                        </Typography>
                    )}
                </Box>

                <FormTextField
                    id={`${name}-notes-input`}
                    name={`${name}.guestNotes`}
                    placeholder={notesPlaceholder}
                    control={control}
                    multiline
                    rows={3}
                    disabled={readOnly}
                    inputProps={{
                        maxLength: 300,
                    }}
                />
            </Box>
            {selectedSetMenu && (
                <>
                    <Divider
                        sx={{
                            mt: 2.5,
                            mb: 2.5,
                            color: theme.palette.faded.main,
                        }}
                    />

                    <Box>
                        <Typography variant='body3' sx={sectionTitleSx}>
                            Selected Set Menu
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                            }}
                        >
                            <Collapse in={!!selectedSetMenu}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: {
                                                xs: 'column',
                                                sm: 'row',
                                            },
                                            gap: 1.5,
                                            alignItems: {
                                                xs: 'flex-start',
                                                sm: 'center',
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >
                                            <Typography
                                                variant='body3'
                                                sx={{
                                                    ...reservationEditValueSx,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {selectedSetMenu.name}
                                            </Typography>

                                            {selectedSetMenu.description ? (
                                                <Typography
                                                    variant='body3'
                                                    sx={{
                                                        ...reservationEditDescriptionSx,
                                                        mt: 0.75,
                                                    }}
                                                >
                                                    {
                                                        selectedSetMenu.description
                                                    }
                                                </Typography>
                                            ) : null}
                                        </Box>

                                        <Typography
                                            variant='body3'
                                            sx={{
                                                ...reservationEditValueSx,
                                                fontWeight: 700,
                                                whiteSpace: 'nowrap',
                                                ml: {
                                                    sm: 'auto',
                                                },
                                            }}
                                        >
                                            {formatCurrencyShort(
                                                selectedSetMenu.pricePerPerson,
                                            )}{' '}
                                            pp
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                height: 38,
                                                minWidth: 112,
                                                overflow: 'hidden',
                                                border: '1px solid',
                                                borderColor:
                                                    'custom.lightBorderColor',
                                                borderRadius: 999,
                                                px: 0.5,
                                                backgroundColor:
                                                    'background.paper',
                                            }}
                                        >
                                            <Button
                                                variant='text'
                                                size='small'
                                                disabled={readOnly}
                                                onClick={
                                                    handleDecrementSetmenuQuantity
                                                }
                                                sx={{
                                                    minWidth: 0,
                                                    width: 34,
                                                    height: 34,
                                                    borderRadius: '50%',
                                                    p: 0,
                                                }}
                                            >
                                                {quantity > 1 ? (
                                                    <Typography
                                                        variant='body3'
                                                        fontWeight={700}
                                                    >
                                                        -
                                                    </Typography>
                                                ) : (
                                                    <DeleteOutlineIcon
                                                        sx={{
                                                            fontSize: 18,
                                                        }}
                                                    />
                                                )}
                                            </Button>

                                            <Typography
                                                variant='body3'
                                                sx={{
                                                    fontWeight: 700,
                                                    minWidth: 20,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {quantity}
                                            </Typography>

                                            <Button
                                                variant='text'
                                                size='small'
                                                disabled={readOnly}
                                                onClick={() =>
                                                    setValue(
                                                        `${name}.quantity`,
                                                        quantity + 1,
                                                    )
                                                }
                                                sx={{
                                                    minWidth: 0,
                                                    width: 34,
                                                    height: 34,
                                                    borderRadius: '50%',
                                                    p: 0,
                                                }}
                                            >
                                                <Typography
                                                    variant='body3'
                                                    fontWeight={700}
                                                >
                                                    +
                                                </Typography>
                                            </Button>
                                        </Box>
                                    </Box>
                                </Box>
                            </Collapse>
                        </Box>
                    </Box>
                </>
            )}
            <Divider
                sx={{
                    mt: 2.5,
                    mb: 2.5,
                    color: theme.palette.faded.main,
                }}
            />
            <Paper
                variant='outlined'
                sx={{
                    borderRadius: 2,
                    borderColor: 'custom.lightBorderColor',
                    overflow: 'hidden',
                    boxShadow: 'none',
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: lighten(
                            theme.palette.primary.main,
                            0.95,
                        ),
                    }}
                >
                    <Typography variant='body3' sx={reservationEditLabelSx}>
                        Total (ex. GST)
                    </Typography>
                    <Typography
                        variant='body3'
                        sx={{
                            ...reservationEditValueSx,
                            fontWeight: 800,
                            ...(readOnly && {
                                textDecoration: 'line-through',
                                textDecorationThickness: '2px',
                            }),
                        }}
                    >
                        {formatCurrencyShort(jobTotal)}
                    </Typography>
                </Box>
            </Paper>
            {(hasPricingTemplates || hasSetMenus) && !isItineraryForm && (
                <>
                    <Divider
                        sx={{
                            mt: 2.5,
                            mb: 2.5,
                            color: theme.palette.faded.main,
                        }}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            flexDirection: { xs: 'column-reverse', sm: 'row' },
                            gap: 1,
                        }}
                    >
                        {!status ||
                        status === Status.PendingBooking ||
                        status === Status.Pending ||
                        status === Status.Accepted ||
                        status === Status.Active ? (
                            <Button
                                color='error'
                                variant='outlined'
                                onClick={handleDelete}
                                fullWidth={isMobile}
                                sx={{
                                    borderRadius: 2,
                                    width: { xs: '100%', sm: 'auto' },
                                }}
                            >
                                Cancel request
                            </Button>
                        ) : (
                            <Box />
                        )}
                    </Box>
                </>
            )}{' '}
            {!readOnly ? (
                <JobMenuModal
                    open={showMenu}
                    onClose={handleMenu}
                    name={name}
                    templates={templates ?? []}
                    onItemsChange={syncJobDateWithCutoff}
                    bookingAvailability={bookingAvailability}
                    providerType={providerType}
                    numberOfGuests={numberOfGuests}
                    setMenus={
                        experienceDetails?.setMenus.filter(
                            (setMenu) =>
                                setMenu.serviceType ===
                                selectedProvider?.services.type,
                        ) ?? []
                    }
                />
            ) : null}
        </Box>
    );
};

export default ReservationJobForm;
